import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Run } from "@quantizr-engineering/common/types/run";
import {
  ContractType,
  Show,
  VenueExpenseTag,
  ShowStatus,
} from "@quantizr-engineering/common/types/show";
import { useRunService } from "../../api/runService";
import { useShowService } from "../../api/showService";
import { PiSparkle } from "react-icons/pi";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import FinancesTable from "../../components/FinancesTable";
import { Form, Field, FieldInputProps, FormikProps } from "formik";
import StatusStepper from "../../components/StatusStepper";
import { prettyDate } from "../../utils/dateFormatters.ts";
import { useAiService } from "../../api/askQz";
import { AskQzModal } from "../../components/AskQzModal";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { calculateShowFigures } from "@quantizr-engineering/common/math/live";
import InfoSection from "../../components/InfoSection";
import DocumentsTable from "../../components/DocumentsTable";
import { enumToOptions } from "../../utils/enumToOptions";
import { greaterOfNumbers } from "../../utils/utils";
import {
  getStatusStyling,
  isShowInActualStatus,
  STATUS_CONFIG,
} from "../../utils/showStatus.ts";
import FileModal from "../../components/FileModal.tsx";
import { FiChevronDown, FiFilePlus } from "react-icons/fi";
import { formatCurrency } from "../../utils/numberFormatters.tsx";
import FileProcessingStatusModal from "../../components/FileProcessingStatusModal.tsx";
import { FileEntity } from "@quantizr-engineering/common/types/file";
import SummaryTable from "../../components/SummaryTable.tsx";
import { useShowDetailsCalculations } from "../../utils/useShowDetailsCalculations.ts";
import DropdownMenuWithHeroButton from "../../components/DropdownMenuWithHeroButton.tsx";
import { FaFilePdf, FaTrash } from "react-icons/fa";
import { toast } from "sonner";
import ReusableFormikWrapper from "../../contexts/ReusableFormikWrapper.tsx";
import { showValidationSchema } from "../../validation/schemas/showValidationSchema.ts";
import EmblaCarousel from "../../components/EmblaCarousel.tsx";
import ProgressBarGroup from "../../components/ProgressBar/ProgressBarGroup.tsx";
import ValuesCard from "../../components/ValuesCard.tsx";
import { showInsights } from "../../components/insights/showInsights.tsx";
import file06 from "../../assets/file06.svg";
import FormErrorSummary from "../../components/ErrorSummarySummary.tsx";
import TextCell from "../../components/TextCell.tsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);
interface AskQzState {
  message: string;
  response: string;
  loading: boolean;
  modalOpen: boolean;
}

const TABS = [
  { id: "showFinances", label: "Show Finances" },
  { id: "showDetails", label: "Show Details" },
  { id: "relatedItems", label: "Related Items" },
];

export const ShowDetails: React.FC = () => {
  const { runId, showId } = useParams<{ runId: string; showId: string }>();
  const {
    shows,
    patchShow,
    deleteShow,
    generateSettlementReport,
    generateShowProposalReport,
  } = useShowService();
  const { runs } = useRunService();
  const { generateQzResponse } = useAiService();
  const [activeTab, setActiveTab] =
    useState<(typeof TABS)[number]["id"]>("showFinances");
  const [askQz, setAskQz] = useState<AskQzState>({
    message: "",
    response: "",
    loading: false,
    modalOpen: false,
  });
  const [showFileModal, setShowFileModal] = useState(false);
  const [showFileProcessingStatusModal, setShowFileProcessingStatusModal] =
    useState(false);
  const [processingFiles, setProcessingFiles] = useState<Partial<FileEntity>[]>(
    []
  );
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const actionsDropdownRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const selectedRun = useMemo<Run | null>(
    () =>
      Array.isArray(runs)
        ? runs.find((run) => run.id === parseInt(runId || "")) || null
        : null,
    [runs, runId]
  );

  const selectedShow = useMemo<Show | null>(
    () =>
      Array.isArray(shows)
        ? shows.find((show) => show.id === parseInt(showId || "")) || null
        : null,
    [shows, showId]
  );

  const makeInitialShowValues = (selectedShow: Show): Show => {
    return {
      ...selectedShow,
      expenses: selectedShow.expenses.sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      deductions: selectedShow.deductions ?? [],
      lifts: selectedShow.lifts ?? [],
      ticketAdjustments: selectedShow.ticketAdjustments ?? [],
      productionBuyouts: selectedShow.productionBuyouts ?? [],
      ticketBonuses: selectedShow.ticketBonuses ?? [],
      commissions: selectedShow.commissions ?? [],
      ticketTypes: selectedShow.ticketTypes ?? [],
    };
  };

  const showValues = useMemo(() => {
    if (!selectedShow)
      return {
        ticketRevenuePotential: 0,
        boxOfficeScorePotential: 0,
        sellableCap: 0,
        ticketsSoldProjected: 0,
        ticketsSoldActual: 0,
        venueExpensesProjected: 0,
        venueExpenses: 0,
        variableRevPotential: 0,
        variableRevActual: 0,
        showNetActual: 0,
        showBuyouts: 0,
        bonusesActual: 0,
        walkoutMinimum: 0,
        walkoutProjected: 0,
        walkoutPotential: 0,
        walkout: 0,
        walkoutScoreProjected: 0,
        walkoutScoreActual: 0,
        boxOfficeScoreProjected: 0,
        boxOfficeScoreActual: 0,
        showScoreProjected: 0,
        showScoreActual: 0,
      };
    return calculateShowFigures(selectedShow);
  }, [selectedShow]);

  const AskQzCard2 = () => {
    return (
      <div className="w-full h-[240px] bg-white bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal via-custom-dark-purple to-custom-purple text-white p-6 relative">
        <div className="card-body p-0 flex flex-col h-full">
          <div className="text-2xl">
            <PiSparkle />
          </div>
          <div className="text-xl font-semibold leading-snug">
            Want more insights or clarity on your data? <br /> Ask Qz.
          </div>
          <div className="mt-auto flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setAskQz((prev) => ({ ...prev, modalOpen: true }))}
              className="btn rounded-full flex items-center justify-center font-bold bg-white text-black border-none 
             hover:bg-gray-100 focus:bg-gray-100 
             hover:opacity-60 focus:opacity-60 transition"
            >
              <PiSparkle />
              <span className="text-center text-sm md:text-base">Ask Qz</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const carouselItems2 = useMemo(() => {
    if (!selectedShow) return [];
    return [
      ...showInsights(selectedShow, shows || []),
      <AskQzCard2 key="ask-qz-card" />,
    ];
  }, [selectedShow, shows]);

  if (!selectedShow) {
    return <div>Show not found</div>;
  }

  const handleAskQz = async () => {
    setAskQz((prev) => ({ ...prev, loading: true }));
    try {
      const response = await generateQzResponse(askQz.message, selectedRun, [
        selectedShow,
      ]);
      setAskQz((prev) => ({ ...prev, response }));
    } finally {
      setAskQz((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <>
      <div className="flex flex-col bg-white max-h-screen max-w-full overflow-y-auto">
        <div className="w-full">
          {showFileModal && (
            <FileModal
              initialShowId={selectedShow.id}
              initialRunId={selectedShow.runID}
              onClose={(fileEntities: Partial<FileEntity>[]) => {
                setProcessingFiles(fileEntities);
                setShowFileModal(false);
                if (fileEntities?.length > 0) {
                  setShowFileProcessingStatusModal(true);
                }
              }}
              defaultType={ContractType.LIVE_MUSIC_SETTLEMENT}
            />
          )}

          {showFileProcessingStatusModal && (
            <FileProcessingStatusModal
              files={processingFiles}
              onClose={() => {
                setShowFileProcessingStatusModal(false);
              }}
            />
          )}
          {askQz.modalOpen === true && showId && (
            <AskQzModal
              isOpen={askQz.modalOpen}
              onClose={() =>
                setAskQz((prev) => ({
                  ...prev,
                  modalOpen: false,
                  message: "",
                  response: "",
                }))
              }
              message={askQz.message}
              response={askQz.response}
              loading={askQz.loading}
              onMessageChange={(message) =>
                setAskQz((prev) => ({ ...prev, message }))
              }
              onSubmit={handleAskQz}
            />
          )}

          <ReusableFormikWrapper
            data={makeInitialShowValues(selectedShow)}
            validationSchema={showValidationSchema}
            onSubmitHandler={async (values, { setSubmitting, resetForm }) => {
              const updatedValues = { ...values };
              updatedValues.commissions = values.commissions?.map(
                (commission) => ({
                  ...commission,
                  percent: Number(commission.percent) || 0,
                })
              );

              updatedValues.guarantee = Array.isArray(values.guarantee)
                ? values.guarantee[0]
                : values.guarantee;

              try {
                setSubmitting(true);
                await patchShow(updatedValues);
              } catch (error) {
                console.error("Error updating show:", error);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values }) => {
              const dateDisplay =
                values.performanceDate !== undefined &&
                values.performanceDate !== null
                  ? prettyDate(values.performanceDate)
                  : { day: "TBD", month: "TBD" };
              const walkoutPotential =
                showValues.walkoutPotential !== undefined &&
                showValues.walkoutPotential !== null
                  ? formatCurrency(showValues.walkoutPotential)
                  : "N/A";
              const walkout =
                showValues.walkout !== undefined && showValues.walkout !== null
                  ? formatCurrency(showValues.walkout)
                  : "N/A";
              const difference =
                showValues.walkout !== undefined && showValues.walkout !== null
                  ? formatCurrency(
                      showValues.walkout - (showValues.walkoutPotential ?? 0)
                    )
                  : "N/A";

              const { showDetailsAccordions } =
                useShowDetailsCalculations(values);
              const statusClass = getStatusStyling(values.status);
              //update this with the data... or do a cleaner way with a separate file

              const cardsData = [
                {
                  status: ShowStatus.DRAFT,
                  walkout: showValues.walkoutPotential,
                  boxOfficeScore: showValues.boxOfficeScorePotential,
                  ticketsSold: showValues.sellableCap,
                  sellableCap: showValues.sellableCap,
                  walkoutTooltipText: "Walkout Potential",
                  qzScoreTooltipText: "Maximum Potential Score",
                  boxOfficeTooltipText:
                    "Walkout Potential as a function of Box Office Potential",
                  ticketsSoldTooltipText: "Maximum Ticket Potential",
                },
                {
                  status: ShowStatus.CONFIRMED,
                  walkout: showValues.walkoutProjected,
                  walkoutMinimum: showValues.walkoutMinimum,
                  walkoutPotential: showValues.walkoutPotential,
                  boxOfficeScore: showValues.boxOfficeScoreProjected,
                  showScore: showValues.showScoreProjected,
                  ticketsSold: showValues.ticketsSoldProjected,
                  sellableCap: showValues.sellableCap,
                  walkoutTooltipText: "Based on Projected Ticket Sales",
                  qzScoreTooltipText: "Projected Success Score",
                  boxOfficeTooltipText:
                    "Walkout Projection as a function of Projected Box Office Sales",
                  ticketsSoldTooltipText: "Projected Tickets Sold / Capacity",
                },
                {
                  status: ShowStatus.SETTLED,
                  walkout: showValues.walkout,
                  walkoutMinimum: showValues.walkoutMinimum,
                  walkoutPotential: showValues.walkoutPotential,
                  boxOfficeScore: showValues.boxOfficeScoreActual,
                  showScore: showValues.showScoreActual,
                  ticketsSold: showValues.ticketsSoldActual,
                  sellableCap: showValues.sellableCap,
                  walkoutTooltipText: "Based on Tickets Reported Sold",
                  qzScoreTooltipText: "Success Score based on Reported Actuals",
                  boxOfficeTooltipText:
                    "Actual Walkout as a function of Actual Box Office Sales",
                  ticketsSoldTooltipText: "Actual Sold Count / Capacity",
                },
              ];

              const cards = cardsData.map((card, idx) => (
                <ValuesCard
                  key={idx}
                  walkoutMinimum={card.walkoutMinimum}
                  walkout={card.walkout}
                  walkoutPotential={card.walkoutPotential}
                  boxOfficeScore={card.boxOfficeScore}
                  showScore={card.showScore}
                  status={card.status}
                  ticketsSold={card.ticketsSold}
                  sellableCap={card.sellableCap}
                  description2="abc"
                  statusClass={getStatusStyling(card.status)}
                  walkoutTooltipText={card.walkoutTooltipText}
                  qzScoreTooltipText={card.qzScoreTooltipText}
                  boxOfficeTooltipText={card.boxOfficeTooltipText}
                  ticketsSoldTooltipText={card.ticketsSoldTooltipText}
                />
              ));

              return (
                <Form
                  className="flex flex-col w-full min-w-0 space-y-2"
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      e.target instanceof HTMLInputElement
                    ) {
                      e.preventDefault();

                      // Skip date inputs since DateCell handles them
                      if (
                        e.target.type === "date" ||
                        e.target.name === "performanceDate"
                      ) {
                        return;
                      }

                      e.currentTarget.dispatchEvent(
                        new Event("submit", { cancelable: true, bubbles: true })
                      );
                    }
                  }}
                >
                  <div className="px-4 pt-2">
                    <Breadcrumbs
                      items={[
                        { text: "Live", link: "/runs" },
                        {
                          text: selectedRun?.name || "",
                          link: `/runs/${runId}`,
                        },
                        {
                          text: values?.venue || "unknown",
                        },
                      ]}
                    />
                  </div>

                  <header className=" px-4 pb-1 sticky top-0 bg-white z-10 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col w-1/2 min-w-[50vw]">
                        {/* Top row: Date and Venue */}
                        <div className="flex gap-4 w-full mb-4">
                          <div className="flex flex-row">
                            <div className="flex flex-col items-center justify-center border rounded-xl w-[90px] h-[80px] px-4 py-2">
                              {values.performanceDate ? (
                                <>
                                  <span className="text-2xl font-bold leading-none">
                                    {dateDisplay.day}
                                  </span>
                                  <span className="text-sm text-[#48455B] mt-1">
                                    {dateDisplay.month}
                                  </span>
                                </>
                              ) : (
                                <span className="text-lg font-bold leading-none">
                                  TBD
                                </span>
                              )}
                            </div>

                            <div className="ml-4 flex flex-col justify-center w-full">
                              {values.venue ? (
                                <Field name="venue">
                                  {({
                                    field,
                                  }: {
                                    field: FieldInputProps<string>;
                                  }) => (
                                    <TextCell
                                      {...field}
                                      customStyles="text-2xl font-semibold text-black w-full px-0"
                                    />
                                  )}
                                </Field>
                              ) : (
                                <span className="text-2xl font-semibold text-black">
                                  Venue: TBD
                                </span>
                              )}
                              <span className="text-xs text-[#48455B]">
                                {values.city ?? "TBD"}, {values.state ?? "TBD"},
                                USA
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Bottom row: StatusStepper and dropdown */}
                        <div className="flex items-center gap-4 max-w-[400px]">
                          <div className="flex-1">
                            <StatusStepper currentStatus={values.status} />
                          </div>
                          <div className="relative">
                            <div
                              className={`px-2 py-1 rounded-lg inline-flex items-center cursor-pointer ${getStatusStyling(
                                values.status
                              )}`}
                              onClick={() =>
                                setStatusDropdownOpen(!statusDropdownOpen)
                              }
                            >
                              <span
                                className={`text-sm font-semibold whitespace-nowrap `}
                              >
                                {values.status}
                              </span>
                              <svg
                                className="w-4 h-4 ml-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>

                            {statusDropdownOpen && (
                              <Field name="status">
                                {({
                                  field,
                                  form,
                                }: {
                                  field: FieldInputProps<string>;
                                  form: FormikProps<any>;
                                }) => {
                                  return (
                                    <div className="absolute mt-1 bg-white shadow-lg rounded-lg w-full z-20">
                                      {Object.keys(STATUS_CONFIG).map(
                                        (statusKey) => (
                                          <div
                                            key={statusKey}
                                            className={`cursor-pointer px-2 py-1 flex items-center hover:bg-gray-50 rounded-lg`}
                                            onClick={() => {
                                              field.onChange({
                                                target: {
                                                  name: field.name,
                                                  value: statusKey,
                                                },
                                              });
                                              form.handleSubmit();
                                              setStatusDropdownOpen(false);
                                            }}
                                          >
                                            <span
                                              className={`text-sm font-semibold flex items-center justify-between ${getStatusStyling(
                                                statusKey
                                              )} p-1 rounded-lg`}
                                            >
                                              {statusKey}
                                            </span>
                                            {values.status === statusKey && (
                                              <svg
                                                className="w-4 h-4 ml-1"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M5 13l4 4L19 7"
                                                />
                                              </svg>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  );
                                }}
                              </Field>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Right side actions remain the same */}
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() =>
                            setAskQz((prev) => ({ ...prev, modalOpen: true }))
                          }
                          className="btn w-full md:w-auto rounded-full flex items-center justify-center font-bold px-2 py-1 min-w-[100px] max-w-[100px]"
                        >
                          <span className="text-[20px]">
                            <PiSparkle />
                          </span>
                          <span className="text-center text-sm md:text-sm">
                            Ask Qz
                          </span>
                        </button>
                        <DropdownMenuWithHeroButton
                          rightIcon={<FiChevronDown className="text-[20px]" />}
                          buttonText="Actions"
                          items={[
                            {
                              icon: <FiFilePlus className="text-lg" />,
                              text: "Upload Files",
                              onClick: () => setShowFileModal(true),
                            },
                            {
                              icon: <FaTrash className="text-lg" />,
                              text: "Delete This Show",
                              onClick: () => {
                                toast("Delete this show?", {
                                  duration: 10000,
                                  action: {
                                    label: "Confirm",
                                    onClick: async () => {
                                      await deleteShow(selectedShow);
                                      navigate(`/runs/${runId}`);
                                      toast.success("Show deleted!");
                                    },
                                  },
                                  cancel: {
                                    label: "Cancel",
                                    onClick: () => {},
                                  },
                                });
                              },
                            },
                            {
                              icon: (
                                <img
                                  src={file06}
                                  alt="Send Settlement Report"
                                  className="w-5 h-5"
                                />
                              ),
                              text: isShowInActualStatus(selectedShow)
                                ? "Send Settlement Report"
                                : "Send Show Proposal",
                              onClick: () => {
                                toast(
                                  <div className="flex flex-col gap-2">
                                    <span>Email To Send To?</span>
                                    <input
                                      type="email"
                                      className="px-2 py-1 border rounded text-black"
                                      placeholder="Enter email address"
                                      id="settlement-email-input"
                                      autoFocus
                                    />
                                  </div>,
                                  {
                                    duration: 30000,
                                    action: {
                                      label: "Send",
                                      onClick: async () => {
                                        const emailInput =
                                          document.getElementById(
                                            "settlement-email-input"
                                          ) as HTMLInputElement;
                                        const emailAddress = emailInput?.value;
                                        if (emailAddress) {
                                          toast.info(
                                            `Sending report to ${emailAddress}...`
                                          );
                                          isShowInActualStatus(selectedShow)
                                            ? generateSettlementReport(
                                                selectedShow,
                                                emailAddress
                                              )
                                            : generateShowProposalReport(
                                                selectedShow,
                                                emailAddress
                                              );
                                        } else {
                                          toast.error(
                                            "Please enter a valid email address"
                                          );
                                        }
                                      },
                                    },
                                    cancel: {
                                      label: "Cancel",
                                      onClick: () => {},
                                    },
                                  }
                                );
                              },
                            },
                          ]}
                        />
                      </div>
                    </div>
                    <div className="px-4">
                      <FormErrorSummary />
                    </div>
                  </header>

                  <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <div className="grid gap-4 w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-12 px-4">
                      <div className="flex flex-col gap-4 col-span-1 md:col-span-1 lg:col-span-3 h-[240px]">
                        <EmblaCarousel
                          slides={cards}
                          startIndex={
                            selectedShow.status === ShowStatus.DRAFT
                              ? 0
                              : selectedShow.status === ShowStatus.CONFIRMED
                              ? 1
                              : 2
                          }
                          border={true}
                        />
                      </div>
                      <div className="col-span-1 md:col-span-1 lg:col-span-3 h-[240px]">
                        <EmblaCarousel slides={carouselItems2} border={true} />
                      </div>

                      <div className="col-span-1 md:col-span-2 lg:col-span-6 rounded-lg border border-gray-300 bg-white">
                        <div className="flex flex-col h-full">
                          <div className="h-full">
                            <ProgressBarGroup
                              title="Walkout"
                              items={[
                                {
                                  label: "Potential",
                                  actual: showValues.walkoutPotential,
                                  total: greaterOfNumbers([
                                    showValues.walkoutPotential,
                                    showValues.walkoutProjected,
                                    showValues.walkout,
                                  ]),
                                  actualGradientColor: "#7D7899",
                                  height: 20,
                                },
                                {
                                  label: "Projected",
                                  actual: showValues.walkoutProjected,
                                  total: greaterOfNumbers([
                                    showValues.walkoutPotential,
                                    showValues.walkoutProjected,
                                    showValues.walkout,
                                  ]),
                                  actualGradientColor: "#A994FF",
                                  height: 20,
                                },
                                {
                                  label: "Actual",
                                  actual: showValues.walkout,
                                  total: greaterOfNumbers([
                                    showValues.walkoutPotential,
                                    showValues.walkoutProjected,
                                    showValues.walkout,
                                  ]),
                                  actualGradientColor: "#35C1ED",
                                  height: 20,
                                },
                              ]}
                            />
                          </div>
                          <div className="h-full">
                            <ProgressBarGroup
                              title="Show Expenses"
                              items={[
                                {
                                  label: "Estimated",
                                  actual: showValues.venueExpensesProjected,
                                  total: greaterOfNumbers([
                                    showValues.venueExpenses,
                                    showValues.venueExpensesProjected,
                                  ]),
                                  actualGradientColor: "#A994FF",
                                  height: 20,
                                },
                                {
                                  label: "Actual",
                                  actual: showValues.venueExpenses,
                                  total: greaterOfNumbers([
                                    showValues.venueExpenses,
                                    showValues.venueExpensesProjected,
                                  ]),
                                  actualGradientColor: "#35C1ED",
                                  height: 20,
                                },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex border-b border-gray-200 pt-2">
                      {TABS.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          tabIndex={-1}
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                      px-4 
                      pt-2
                      mt-2
                      text-[#48455B] 
                      border-b-2 
                      transition-colors 
                      duration-200 
                      hover:text-[#6D53FD] 
                      ${
                        activeTab === tab.id
                          ? "border-[#6D53FD] text-[#6D53FD]"
                          : "border-transparent"
                      }
                    `}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {activeTab === "showFinances" && (
                      <div className="flex flex-col overflow-y-auto overflow-x-hidden h-auto min-h-screen px-4 pt-2 pb-64">
                        <div className="flex-1 flex flex-col min-w-0">
                          <SummaryTable
                            title="Show Summary"
                            accordionsList={showDetailsAccordions()}
                          />
                          <FinancesTable
                            title="Guarantee"
                            data={[
                              { guarantee: String(values.guarantee ?? "N/A") },
                            ]}
                            columns={[
                              {
                                title: "Amount",
                                fieldname: "guarantee",
                                type: "currency",
                              },
                            ]}
                            fieldArrayName="guarantee"
                            blankObjectValue={0}
                            calculateActual={(data) => data.guarantee ?? 0}
                            calculateDifference={(_data) => 0}
                            referenceSelf={true}
                            singular={true}
                          />
                          <FinancesTable
                            title="Buyouts"
                            data={values.productionBuyouts}
                            columns={[
                              {
                                title: "Name",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Amount",
                                fieldname: "amount",
                                type: "currency",
                              },
                            ]}
                            fieldArrayName="productionBuyouts"
                            blankObjectValue={{
                              name: "",
                              amount: 0,
                            }}
                            calculateActual={(data) => data.amount}
                            calculateDifference={(_data) => 0}
                          />
                          <FinancesTable
                            title="Tickets"
                            data={values.ticketTypes}
                            columns={[
                              {
                                title: "Type",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Price",
                                fieldname: "price",
                                type: "number",
                              },
                              {
                                title: "Quantity",
                                fieldname: "quantity",
                                type: "number",
                              },
                              {
                                title: "Projected",
                                fieldname: "soldProjected",
                                type: "number",
                              },
                              {
                                title: "Sold",
                                fieldname: "sold",
                                type: "number",
                              },
                              {
                                title: "Charity Fee",
                                fieldname: "charityFee",
                                type: "number",
                              },
                              {
                                title: "Facility Fee",
                                fieldname: "facilityFee",
                                type: "number",
                              },
                            ]}
                            blankObjectValue={{
                              name: "",
                              price: 0,
                              quantity: 0,
                              sold: 0,
                              charityFee: 0,
                              facilityFee: 0,
                            }}
                            calculateEstimated={(data) => {
                              return data.price * (data.soldProjected ?? 0);
                            }}
                            calculateActual={(data) => {
                              return data.price * (data.sold ?? 0);
                            }}
                            calculateDifference={(data) => {
                              if (!data.sold || !data.quantity) {
                                return 0;
                              }
                              return data.price
                                ? data.price * (data.sold ?? 0) -
                                    data.price * data.soldProjected
                                : 0;
                            }}
                            fieldArrayName="ticketTypes"
                            estimatedColumnName="Projected"
                          />
                          <FinancesTable
                            title="Adjustments"
                            data={values.ticketAdjustments}
                            columns={[
                              {
                                title: "Name",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Notes",
                                fieldname: "notes",
                                type: "text",
                              },
                              {
                                title: "Amount",
                                fieldname: "amount",
                                type: "currency",
                              },
                              {
                                title: "Apply To Gross",
                                fieldname: "addToGross",
                                type: "checkbox",
                              },
                            ]}
                            fieldArrayName="ticketAdjustments"
                            blankObjectValue={{
                              name: "",
                              amount: 0,
                              addToGross: false,
                            }}
                            calculateActual={(data) => data.amount}
                            calculateDifference={(_data) => 0}
                          />
                          <FinancesTable
                            title="Bonuses"
                            data={values.ticketBonuses}
                            columns={[
                              {
                                title: "Threshold",
                                fieldname: {
                                  amount: "threshold",
                                  percent: "thresholdPercent",
                                },
                                type: "threshold",
                              },
                              {
                                title: "Award",
                                fieldname: {
                                  amount: "bonusAmount",
                                  percent: "bonusPercent",
                                },
                                type: "award",
                              },
                            ]}
                            calculateEstimated={(data) => {
                              if (data.bonusAmount > 0) {
                                return data.bonusAmount;
                              } else if (data.bonusPercent > 0) {
                                return (
                                  (data.bonusPercent / 100) *
                                  calculateShowFigures(selectedShow)
                                    .ticketRevenuePotential
                                );
                              }
                              return 0;
                            }}
                            calculateActual={(data) => {
                              if (
                                !!data.threshold &&
                                calculateShowFigures(selectedShow)
                                  .ticketsSoldActual >= data.threshold
                              ) {
                                return data.bonusAmount
                                  ? data.bonusAmount
                                  : (data.bonusPercent *
                                      calculateShowFigures(selectedShow)
                                        .ticketRevenueActual) /
                                      100;
                              } else if (
                                !!data.thresholdPercent &&
                                (calculateShowFigures(selectedShow)
                                  .ticketsSoldActual /
                                  calculateShowFigures(selectedShow)
                                    .sellableCap) *
                                  100 >=
                                  data.thresholdPercent
                              ) {
                                return data.bonusAmount
                                  ? data.bonusAmount
                                  : (data.bonusPercent *
                                      calculateShowFigures(selectedShow)
                                        .ticketRevenueActual) /
                                      100;
                              } else {
                                return 0;
                              }
                            }}
                            calculateDifference={(_data) => 0}
                            fieldArrayName="ticketBonuses"
                            blankObjectValue={{
                              bonusAmount: 0,
                              threshold: 0,
                              thresholdPercent: 0,
                              bonusPercent: 0,
                            }}
                            estimatedColumnName="Potential"
                          />
                          <FinancesTable
                            title="Deductions"
                            data={values.deductions}
                            columns={[
                              {
                                title: "Name",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Amount",
                                fieldname: "amount",
                                type: "currency",
                              },
                              {
                                title: "Apply To Gross",
                                fieldname: "addToGross",
                                type: "checkbox",
                              },
                            ]}
                            fieldArrayName="deductions"
                            blankObjectValue={{
                              name: "",
                              amount: 0,
                              addToGross: false,
                            }}
                            calculateActual={(data) => data.amount}
                            calculateDifference={(_data) => 0}
                          />
                          <FinancesTable
                            title="Deposits"
                            data={values.deposits}
                            columns={[
                              {
                                title: "Deposit",
                                fieldname: "amount",
                                type: "currency",
                              },
                            ]}
                            calculateActual={(data) => data?.amount ?? "-"}
                            calculateDifference={(_data) => 0}
                            fieldArrayName="deposits"
                            blankObjectValue={{ amount: 0 }}
                          />
                          <FinancesTable
                            title="Expenses"
                            data={values.expenses}
                            columns={[
                              {
                                title: "Name",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Tag",
                                fieldname: "tag",
                                type: "select",
                                selectOptions: enumToOptions(VenueExpenseTag),
                              },
                              {
                                title: "Note",
                                fieldname: "notes",
                                type: "text",
                              },
                              {
                                title: "Budget",
                                fieldname: "cost",
                                type: "currency",
                              },
                              {
                                title: "Actual",
                                fieldname: "actualCost",
                                type: "currency",
                              },
                            ]}
                            calculateEstimated={(data) =>
                              data.cost * data.quantity
                            }
                            calculateActual={(data) => data.actualCost ?? 0}
                            blankObjectValue={{
                              name: "",
                              note: "",
                              tag: VenueExpenseTag.MISC,
                              quantity: 1,
                              cost: 0,
                              actualCost: 0,
                            }}
                            invertColors={true}
                            calculateDifference={(data) => {
                              if (!data.actualCost || !data.cost) {
                                return 0;
                              }
                              return (
                                data.actualCost - data.cost * data.quantity
                              );
                            }}
                            fieldArrayName="expenses"
                          />
                          <FinancesTable
                            title="Commissions"
                            data={values.commissions}
                            columns={[
                              {
                                title: "Name",
                                fieldname: "name",
                                type: "text",
                              },
                              {
                                title: "Percent",
                                fieldname: "percent",
                                type: "number",
                              },
                              {
                                title: "Buyout",
                                fieldname: "commissionBuyout",
                                type: "currency",
                              },
                              {
                                title: "Amount",
                                fieldname: "flatFee",
                                type: "number",
                              },
                            ]}
                            calculateEstimated={(data) => {
                              return (
                                calculateShowFigures(
                                  selectedShow
                                ).commissionsPotential.commissions?.find(
                                  (commission) => commission.name === data.name
                                )?.amount ?? 0
                              );
                            }}
                            calculateActual={(data) => {
                              return (
                                calculateShowFigures(
                                  selectedShow
                                ).commissionsActual.commissions?.find(
                                  (commission) => commission.name === data.name
                                )?.amount ?? 0
                              );
                            }}
                            calculateDifference={(_data) => 0}
                            fieldArrayName="commissions"
                            blankObjectValue={{
                              name: "",
                              percent: 0,
                              commissionBuyout: 0,
                              amount: 0,
                            }}
                            estimatedColumnName="Potential"
                          />
                        </div>
                      </div>
                    )}
                    {activeTab === "showDetails" && (
                      <div className="w-full rounded-lg px-4 pb-32">
                        <div className="col-span-12 h-64">
                          <InfoSection data={values} />
                        </div>
                      </div>
                    )}
                    {activeTab === "relatedItems" && (
                      <div className="w-full rounded-lg px-4 pb-32">
                        <h3 className="font-bold mb-4">Show Documents</h3>
                        <DocumentsTable files={values.files} />
                      </div>
                    )}
                  </div>
                </Form>
              );
            }}
          </ReusableFormikWrapper>
        </div>
      </div>
    </>
  );
};

export default ShowDetails;
